import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, FileWarning, SearchX, Link2Off, ImageOff, ArrowRight, CheckCircle2, RefreshCw, Radar } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { websiteApi } from './api';

const KIND = {
  unpublished: { icon: FileWarning, label: 'Unpublished changes', tone: 'text-amber-700 bg-amber-50 border-amber-200' },
  seo: { icon: SearchX, label: 'SEO check failing', tone: 'text-red-700 bg-red-50 border-red-200' },
  broken_link: { icon: Link2Off, label: 'Broken links', tone: 'text-red-700 bg-red-50 border-red-200' },
  missing_alt: { icon: ImageOff, label: 'Missing alt text', tone: 'text-sky-700 bg-sky-50 border-sky-200' },
};
// What "Fix" opens: SEO → SEO panel with AI suggestions pre-generated (review-only); broken link → offending section; alt → exact image field + AI suggestion.
const focusFor = (a, d) => a.kind === 'seo' ? { panel: 'seo', ai: true }
  : a.kind === 'broken_link' ? { sectionId: (d || a.details?.[0])?.section_id }
  : a.kind === 'missing_alt' ? { sectionId: (d || a.details?.[0])?.section_id, imageUrl: (d || a.details?.[0])?.url } : null;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'never';
const StatusBadge = ({ status, first }) => status === 'new'
  ? <span className="rounded-full bg-amber-500 text-white text-[10px] font-semibold px-1.5 py-0.5 uppercase" title={`First detected ${fmtDate(first)}`}>New</span>
  : <span className="rounded-full bg-neutral-200 text-neutral-700 text-[10px] font-medium px-1.5 py-0.5" title={`First detected ${fmtDate(first)}`}>Ongoing · since {fmtDate(first)}</span>;

const detailText = (a, d) => a.kind === 'broken_link'
  ? <>“{d.element}” → <code className="bg-neutral-100 px-1">{d.href}</code> — {d.reason}</>
  : <>image <code className="bg-neutral-100 px-1">{d.url.split('/').pop()}</code> in field <code className="bg-neutral-100 px-1">{d.field.replace(/^props\./, '')}</code></>;

// Warns about LIVE builder pages: unpublished changes, failing SEO, broken internal links, images missing alt text.
export const PageHealthAlerts = ({ token, api: givenApi, onOpenPage, title = 'Website Page Health', showHealthy = false }) => {
  const api = givenApi || websiteApi(token);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [scanning, setScanning] = useState(false);
  const load = useCallback(() => api.health().then((d) => { setData(d); setError(false); }).catch(() => setError(true)), [api]);
  useEffect(() => { load(); window.addEventListener('website-pages-changed', load); return () => window.removeEventListener('website-pages-changed', load); }, [load]);

  const scanNow = () => { setScanning(true); api.healthScan().then((r) => { toast.success(`Scan complete — ${r.total} issue${r.total === 1 ? '' : 's'} (${r.new} new, ${r.resolved} resolved)`); load(); }).catch(() => toast.error('Scan failed')).finally(() => setScanning(false)); };

  if (error || !data) return null;
  const { alerts, counts, last_scan: lastScan } = data;
  if (!alerts.length && !showHealthy) return null;

  return (
    <Card className={alerts.length ? 'border-amber-200' : 'border-emerald-200'} data-testid="page-health-alerts">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base gap-3">
          <span className="flex items-center gap-2">
            {alerts.length ? <AlertTriangle className="h-5 w-5 text-amber-600" /> : <CheckCircle2 className="h-5 w-5 text-emerald-600" />}
            {title}
            {alerts.length > 0 && <span className="ml-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5" data-testid="page-health-count">{counts.total}</span>}
            {counts.new > 0 && lastScan && <span className="rounded-full bg-amber-500 text-white text-[10px] font-semibold px-1.5 py-0.5 uppercase" data-testid="page-health-new-count">{counts.new} new</span>}
          </span>
          <span className="flex items-center gap-2 text-xs font-normal text-neutral-500">
            <span data-testid="page-health-last-scan">Nightly scan: {fmtWhen(lastScan?.run_at)}</span>
            <button onClick={scanNow} disabled={scanning} className="inline-flex items-center gap-1 text-neutral-600 hover:text-neutral-900 disabled:opacity-50" title="Run the health scan now" data-testid="page-health-scan-now"><Radar className={`h-4 w-4 ${scanning ? 'animate-pulse' : ''}`} />{scanning ? 'Scanning…' : 'Scan now'}</button>
            <button onClick={load} className="p-1 rounded text-neutral-400 hover:text-neutral-700" title="Refresh" data-testid="page-health-refresh"><RefreshCw className="h-4 w-4" /></button>
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {alerts.length === 0 ? (
          <p className="text-sm text-neutral-600" data-testid="page-health-ok">All live pages are published, pass SEO checks, have no broken links and every image has alt text.</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {alerts.map((a) => {
              const k = KIND[a.kind];
              return (
                <li key={`${a.slug}-${a.kind}`} className="flex items-start gap-3 py-2.5" data-testid={`page-health-alert-${a.slug}-${a.kind}`}>
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap mt-0.5 ${k.tone}`}><k.icon className="h-3 w-3" />{k.label}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 truncate flex items-center gap-2">{a.name} <span className="text-neutral-400 font-normal">{a.path}</span>{!a.details && <StatusBadge status={a.status} first={a.first_detected} />}</p>
                    <p className="text-xs text-neutral-500 truncate">{a.message}{a.kind === 'seo' ? ` · score ${a.seo_score}/100` : ''}</p>
                    {a.details && (
                      <ul className="mt-1 space-y-1" data-testid={`page-health-details-${a.slug}-${a.kind}`}>
                        {a.details.map((d, j) => (
                          <li key={j} className="text-xs text-neutral-600 flex items-start gap-2">
                            <span className="flex-1 min-w-0">Section {d.section_index} “{d.section_title}” · {detailText(a, d)} <StatusBadge status={d.status} first={d.first_detected} /></span>
                            {onOpenPage && a.details.length > 1 && <button className="text-amber-700 underline whitespace-nowrap" onClick={() => onOpenPage(a.slug, focusFor(a, d))} data-testid={`page-health-open-${a.slug}-${a.kind}-${j}`}>Fix</button>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {onOpenPage && <Button size="sm" variant="outline" onClick={() => onOpenPage(a.slug, focusFor(a))} data-testid={`page-health-open-${a.slug}-${a.kind}`}>Fix <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
